import * as React from 'react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';

interface NextLinkComposedProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    Omit<
      NextLinkProps,
      | 'href'
      | 'as'
      | 'passHref'
      | 'onMouseEnter'
      | 'onClick'
      | 'onTouchStart'
      | 'legacyBehavior'
    > {
  to: NextLinkProps['href'];
  linkAs?: NextLinkProps['as'];
  linkTarget?: string;
}

export const NextLinkComposed = React.forwardRef<
  HTMLAnchorElement,
  NextLinkComposedProps
>((props, ref) => {
  const {
    to,
    linkAs,
    replace,
    scroll,
    shallow,
    prefetch,
    locale,
    linkTarget,
    onClick,
    ...other
  } = props;

  const handleClick = onClick
    ? async (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      ): Promise<void> => {
        e.preventDefault();
        await onClick(e);

        const isExternal =
          typeof to === 'string' &&
          (to.indexOf('http') === 0 ||
            to.indexOf('mailto:') === 0 ||
            to.indexOf('sms:') === 0);

        if (isExternal) {
          if (linkTarget === '_self') {
            window.location.href = to;
          } else {
            window.open(to, '_blank');
          }
        } else {
          window.history.pushState(window.history.state, '', to as string);
        }
      }
    : undefined;

  return (
    <NextLink
      ref={ref}
      href={to}
      prefetch={prefetch}
      as={linkAs}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      locale={locale}
      target={linkTarget}
      onClick={handleClick}
      {...other}
    />
  );
});
NextLinkComposed.displayName = 'NextLinkComposed';
